// @workflow assistant.reply-blocks
/**
 * The words and limits of reply blocks (contract "Reply blocks (draft v1)"), defined once:
 * the kinds this build draws (sent as `client.blocks`), the value types table cells and record
 * fields carry, the tones, the ref kinds, and every limit. blockValues.js, blocks.js and the
 * block components import them from here.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

export const BLOCK_KIND = Object.freeze({
  NUMBER: 'number',
  TABLE: 'table',
  RECORD: 'record',
  BREAKDOWN: 'breakdown',
  CALLOUT: 'callout',
  SUGGESTIONS: 'suggestions',
});

/** What this build draws: sent with every message as `client.blocks`, so the server sends only these. */
export const BLOCK_KINDS = Object.freeze(Object.values(BLOCK_KIND));

export const BLOCK_LIMITS = Object.freeze({
  /** Rows a table shows inline; `total` says how many there are ("20 of 1,079"). */
  TABLE_INLINE_ROWS: 20,
  MAX_COLUMNS: 10,
  MIN_TILES: 1,
  MAX_TILES: 4,
  MAX_RECORD_FIELDS: 8,
  MAX_RECORD_ACTIONS: 4,
  MIN_PARTS: 2,
  MAX_PARTS: 8,
  MAX_SUGGESTIONS: 3,
  MAX_LABEL_LENGTH: 80,
  MAX_TEXT_LENGTH: 400,
  MAX_KEY_LENGTH: 40,
  /** A callout's short name lists ("Shown • MER-SCF-27001 • and 1 more"), so the reply text never repeats names. */
  MAX_CALLOUT_LISTS: 2,
  MAX_LIST_ITEMS: 5,
  MAX_LIST_LABEL_LENGTH: 40,
  MAX_LIST_ITEM_LENGTH: 80,
});

/** The server maps every domain value (Warning, Pending, Overdue) to a tone; the UI never guesses from words. */
export const TONE = Object.freeze({ OK: 'ok', INFO: 'info', WARNING: 'warning', DANGER: 'danger', NEUTRAL: 'neutral' });
export const TONES = Object.freeze(Object.values(TONE));
export const CALLOUT_TONES = Object.freeze([TONE.INFO, TONE.WARNING]);

export const VALUE_TYPE = Object.freeze({
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  DATETIME: 'datetime',
  STATUS: 'status',
  REF: 'ref',
  USER: 'user',
  PROGRESS: 'progress',
  BOOLEAN: 'boolean',
});
export const VALUE_TYPES = Object.freeze(Object.values(VALUE_TYPE));

export const REF_KIND = Object.freeze({
  TAG: 'tag',
  TASK: 'task',
  FINDING: 'finding',
  INSPECTION: 'inspection',
  DOCUMENT: 'document',
  REGISTER_ROW: 'register-row',
});
export const REF_KINDS = Object.freeze(Object.values(REF_KIND));

/** How values are written: "14 Oct 2026", "1,079" (en-GB, as the app's dates). */
export const BLOCK_LOCALE = 'en-GB';

/** Every word a block shows, in one place. */
export const BLOCK_TEXT = Object.freeze({
  NONE: '—',
  YES: 'Yes',
  NO: 'No',
  /** The empty state when the server sent no `empty` words. */
  EMPTY: 'Nothing to show.',
  AS_OF: 'as of',
  IN_TOTAL: 'in total',
  EXPORT_CSV: 'Export CSV',
  /** A source action the server sent without words. */
  OPEN: 'Open',
  ASK_NEXT: 'Ask next',
  ROWS: (n) => (n === 1 ? '1 row' : `${n} rows`),
  OF: (shown, total) => `${shown} of ${total}`,
  SORT_BY: (label) => `Sort by ${label}`,
  ASK: 'Ask',
  SHOW_ALL_TASKS: (n) => `Show all ${n} tasks`,
  CHANGE_FROM: 'from',
  CHANGE_TO: 'to',
  /** The last bullet of a callout list when the server left names out; `n` comes formatted. */
  AND_MORE: (n) => `and ${n} more`,
});

/** Why a block was dropped (logged; the reply's text still shows). */
export const BLOCK_INVALID = Object.freeze({
  NOT_AN_OBJECT: 'the block is not an object',
  UNKNOWN_KIND: 'this build does not draw this kind',
  LABEL: 'a label is missing or too long',
  TEXT: 'a text is missing or too long',
  TONE: 'a tone is not one of ok, info, warning, danger, neutral',
  TILES: `it needs ${BLOCK_LIMITS.MIN_TILES} to ${BLOCK_LIMITS.MAX_TILES} tiles`,
  NUMBER: 'a number is not a finite number',
  COLUMNS: `it needs 1 to ${BLOCK_LIMITS.MAX_COLUMNS} columns with a short key, a label and a known type`,
  ROWS: 'the rows are not a list of objects',
  CELL: 'a value does not match its type',
  ROW_ACTION: 'rowAction does not name a ref column',
  TOTAL: 'the total is not a whole number at least the rows shown',
  FIELDS: `it needs at most ${BLOCK_LIMITS.MAX_RECORD_FIELDS} fields, each with a label, a known type and a matching value`,
  ACTIONS: `it needs at most ${BLOCK_LIMITS.MAX_RECORD_ACTIONS} actions this build can run`,
  PARTS: `it needs ${BLOCK_LIMITS.MIN_PARTS} to ${BLOCK_LIMITS.MAX_PARTS} parts with a label and a count of 0 or more`,
  SUGGESTIONS: `it needs 1 to ${BLOCK_LIMITS.MAX_SUGGESTIONS} questions, each with a label and an ask`,
  SOURCE: 'the source has no label, a bad date or an action this build cannot run',
  LINK: 'showAll or export has no label, or nothing this build can open',
  /** Left out, not dropped: the callout still shows its text (OPEN_ITEMS.md: ui-left-out-parts). */
  LISTS: `a callout's lists need 1 to ${BLOCK_LIMITS.MAX_CALLOUT_LISTS} entries, each with a label of up to ${BLOCK_LIMITS.MAX_LIST_LABEL_LENGTH} characters, 1 to ${BLOCK_LIMITS.MAX_LIST_ITEMS} names of up to ${BLOCK_LIMITS.MAX_LIST_ITEM_LENGTH} and a "more" of 0 or more`,
});
